/* eslint-disable react/jsx-no-bind */
/* eslint-disable id-length */
import { memo } from "react";
import { Node, NodeProps } from "@xyflow/react";
import {
  CommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  isWaitingOnConcurrencyGroup,
  isWaitingOnPlatformLimit,
} from "~/lib/pipeline";
import BaseNode from "~/components/Playground/Node/BaseNode";
import Commands from "~/components/Playground/Node/CommandStepNode/Commands";
import Plugins from "~/components/Playground/Node/CommandStepNode/Plugins";
import ArtifactPaths from "~/components/Playground/Node/CommandStepNode/ArtifactPaths";
import { getDockerPlugin } from "~/lib/pipeline/plugins";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import {
  useBuild,
  useJobsForStep,
  useMostRecentMatrixJobsForStep,
  useMostRecentParallelJobsForStep,
} from "~/components/build/Show/lib/BuildContext";
import { StateIcon, StateLabel } from "~/components/Playground/Node/BaseNode/StateIcon";
import { bgColorForStep, textColorForStep } from "~/components/Playground/Node/colors";
import { Jobs } from "~/components/Playground/Node/Jobs";
import { twMerge } from "tailwind-merge";
import { CommandJob } from "~/components/build/Show/lib/types";
import { JobDuration } from "~/components/job/Job/JobDuration";
import { MissingStepDependencies } from "~/components/Playground/Node/MissingStepDependencies";
import { Link } from "react-router-dom";
import CommandStepLabel from "~/components/build/Show/components/CommandStepLabel";
import { JobCount, StatsJobCount } from "~/components/build/Show/components/JobCount";
import { ParallelJobs } from "~/components/Playground/Node/CommandStepNode/ParallelJobs";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { NodeDuration } from "~/components/Playground/Node/NodeDuration";

type CommandStepNodeType = Node<CommandStep, "command">;

function CommandStepNode(props: NodeProps<CommandStepNodeType>) {
  const { build } = useBuild({ strict: false });
  const { missingDependencies } = useStepDependencies(props.id);
  const annotationCountsByStyle = props.data.statistics?.annotationCountsByStyle;

  const parallelJobs = useMostRecentParallelJobsForStep(props.id);
  const matrixJobs = useMostRecentMatrixJobsForStep(props.id);
  const defaultJobs = useJobsForStep<CommandJob>(props.id);

  let jobs: CommandJob[];
  if (isParallelCommandStep(props.data)) {
    jobs = parallelJobs;
  } else if (isMatrixCommandStep(props.data)) {
    jobs = matrixJobs;
  } else {
    jobs = defaultJobs;
  }

  const latestJob = jobs.length ? jobs[jobs.length - 1] : null;

  const { plugins, artifactPaths } = props.data;
  const docker = plugins ? getDockerPlugin(plugins) : null;
  const waitingOnConcurrencyGroup = isWaitingOnConcurrencyGroup(
    latestJob?.state,
    props.data.statistics,
  );
  const waitingOnPlatformLimit = isWaitingOnPlatformLimit(latestJob?.state, props.data.statistics);

  const label = <CommandStepLabel {...props.data} />;

  const duration =
    Features.BuildCanvasDurations &&
    Features.BuildSidebar &&
    props.data.state &&
    isDefaultCommandStep(props.data) ? (
      <NodeDuration
        startedAt={props.data.statistics?.latestJobStartedAt}
        finishedAt={props.data.statistics?.latestJobFinishedAt}
      />
    ) : undefined;

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {build && latestJob && props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            <div className="space-y-3">
              <div>
                <div className="flex items-baseline gap-2">
                  <div className="min-w-0 flex-auto font-medium">{label}</div>
                  <div className="ml-auto flex items-center gap-2">
                    <StateLabel {...props.data} missingDependencies={missingDependencies} />
                    <StateIcon
                      className={textColorForStep({
                        ...props.data,
                        missingDependencies,
                        waitingOnConcurrencyGroup,
                        waitingOnPlatformLimit,
                      })}
                      {...props.data}
                      missingDependencies={missingDependencies}
                      waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
                      waitingOnPlatformLimit={waitingOnPlatformLimit}
                    />
                  </div>
                </div>

                {isDefaultCommandStep(props.data) && (
                  <JobDuration job={latestJob} build={build} className="text-charcoal-300" />
                )}
              </div>

              <MissingStepDependencies missingDependencies={missingDependencies} build={build} />

              <Commands data={props.data} dockerImage={docker?.image} />

              {(isParallelCommandStep(props.data) || isMatrixCommandStep(props.data)) && (
                <div
                  className={twMerge(
                    bgColorForStep(props.data, missingDependencies, build),
                    "-mx-3 space-y-3 border-t border-gray-200/50 bg-white p-3 pb-0",
                  )}
                >
                  <Jobs jobs={jobs} />
                </div>
              )}

              {!Features.BuildSidebar && isDefaultCommandStep(props.data) && (
                <div>
                  <Link
                    to={latestJob.path}
                    onClick={(e) => e.stopPropagation()}
                    className="btn-depth btn-small p-2 no-underline hover:no-underline"
                  >
                    Go to job
                  </Link>
                </div>
              )}
            </div>
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div className="relative">
          <BaseNode
            {...props}
            label={label}
            duration={duration}
            annotationCountsByStyle={annotationCountsByStyle}
            count={
              (isParallelCommandStep(props.data) || isMatrixCommandStep(props.data)) &&
              (Features.ProgressiveBuildShow ? (
                <StatsJobCount step={props.data} />
              ) : (
                <JobCount jobs={jobs} step={props.data} />
              ))
            }
          >
            {build && Features.BuildCanvasParallelJobs && isParallelCommandStep(props.data) && (
              <ParallelJobs step={props.data} />
            )}
            {!props.data.state && <Commands data={props.data} dockerImage={docker?.image} />}
            {plugins && <Plugins plugins={plugins} />}
            {artifactPaths && <ArtifactPaths paths={artifactPaths} />}
          </BaseNode>
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(CommandStepNode);
